import { AccordionItem as HerouiAccordionItem } from "@heroui/react";
import React from "react";

// Types for react-stately collection
type CollectionNode = {
	type: string;
	props: Record<string, unknown>;
	key: string | null;
	rendered?: unknown;
	textValue?: string;
	hasChildNodes?: boolean;
	childNodes?: () => Generator<CollectionNode>;
};

type CollectionContext = {
	expandedKeys?: Set<React.Key>;
	selectedKeys?: Set<React.Key>;
};

type GetCollectionNode = (
	props: Record<string, unknown>,
	context: CollectionContext,
) => Generator<CollectionNode>;

interface WithCollectionProps {
	getCollectionNode: GetCollectionNode;
	getCollectionProps?: () => Record<string, unknown>;
}

type Props = React.ComponentProps<typeof HerouiAccordionItem> & {
	// children may be JSX (with simple inline tags) or a string
	children?: React.ReactNode;
};

const ALLOWED_TAGS = new Set(["strong", "b", "em", "i", "u", "a", "br"]);

function escapeHtml(str: string) {
	return str
		.replace(/&/g, "&amp;")
		.replace(/</g, "&lt;")
		.replace(/>/g, "&gt;")
		.replace(/"/g, "&quot;")
		.replace(/'/g, "&#39;");
}

function nodeToHtml(node: React.ReactNode): string {
	if (node === null || node === undefined || node === false) return "";
	if (typeof node === "string") return escapeHtml(node);
	if (typeof node === "number") return String(node);
	if (Array.isArray(node)) return node.map(nodeToHtml).join("");
	if (React.isValidElement(node)) {
		const type =
			typeof node.type === "string"
				? node.type
				: (node.type as { displayName?: string }).displayName || "unknown";
		const children = node.props?.children;
		const inner = nodeToHtml(children);

		// handle simple intrinsic elements
		if (typeof type === "string") {
			const tag = type.toLowerCase();
			if (ALLOWED_TAGS.has(tag)) {
				if (tag === "a") {
					const href = node.props?.href ? String(node.props.href) : "";
					const safeHref = escapeHtml(href);
					return `<a href=\"${safeHref}\">${inner}</a>`;
				}
				if (tag === "br") return "<br/>";
				return `<${tag}>${inner}</${tag}>`;
			}
			// for non-whitelisted tags, render only their children
			return inner;
		}

		// for custom React components, try to render children only
		return inner;
	}

	// fallback: string conversion
	try {
		return String(node);
	} catch (e) {
		return "";
	}
}

const AccordionItemHtml = (props: Props) => {
	const { children, ...rest } = props;
	const html = nodeToHtml(children ?? "");

	return (
		<HerouiAccordionItem {...(rest as Record<string, unknown>)}>
			{/* eslint-disable-next-line react/no-danger */}
			<div dangerouslySetInnerHTML={{ __html: html }} />
		</HerouiAccordionItem>
	);
};

// Provide a getCollectionNode implementation on the wrapper so react-stately
// treats this as a single collection item and does not descend into its
// children (which may contain DOM elements like <strong> that would cause
// "Unknown element <strong> in collection.").
const getCollectionNode: GetCollectionNode = function getCollectionNode(
	props,
	_context,
) {
	function* gen() {
		// Use provided key if available; react-stately will compute one if not.
		yield {
			type: "item",
			props: props,
			// Don't attempt to read `key` from props — React treats `key` as a special prop
			// and it won't be present on props. Let react-stately compute the key if needed.
			key: null,
			// rendered/textValue are optional; provide a text fallback so searching/screen readers work.
			rendered: props?.children ?? props?.title ?? "",
			textValue: typeof props?.children === "string" ? props.children : "",
		};
	}
	return gen();
};

// Add the collection helpers to the component (type-safe)
(AccordionItemHtml as unknown as WithCollectionProps).getCollectionNode =
	getCollectionNode;

// Also forward getCollectionProps if the original component provides it (best-effort).
const heroui = HerouiAccordionItem as unknown as WithCollectionProps;
if (typeof heroui.getCollectionProps === "function") {
	(AccordionItemHtml as unknown as WithCollectionProps).getCollectionProps =
		heroui.getCollectionProps;
}

export default AccordionItemHtml;
